/**
 * 纯 JS XLSX 解析器（零依赖）
 * 利用浏览器 DecompressionStream API 解压 ZIP + DOMParser 解析 XML
 * 支持 .xlsx 和 .csv 格式，不支持旧版 .xls 二进制格式
 */

// ========== ZIP 解压 ==========

/**
 * 从 ZIP ArrayBuffer 中提取指定文件名的文本内容
 */
async function extractZipEntry(buffer, targetName) {
  const view = new DataView(buffer)
  const bytes = new Uint8Array(buffer)

  // 查找 End of Central Directory (EOCD) 签名 0x06054b50
  let eocdOffset = -1
  for (let i = buffer.byteLength - 22; i >= Math.max(0, buffer.byteLength - 65557); i--) {
    if (view.getUint32(i, true) === 0x06054b50) {
      eocdOffset = i
      break
    }
  }
  if (eocdOffset === -1) {
    throw new Error('无效的 ZIP 文件：未找到 EOCD 记录')
  }

  const totalEntries = view.getUint16(eocdOffset + 10, true)
  const cdOffset = view.getUint32(eocdOffset + 16, true)

  // 遍历 Central Directory
  let pos = cdOffset
  for (let i = 0; i < totalEntries; i++) {
    if (view.getUint32(pos, true) !== 0x02014b50) break

    const compressionMethod = view.getUint16(pos + 10, true)
    const compressedSize = view.getUint32(pos + 20, true)
    const fileNameLen = view.getUint16(pos + 28, true)
    const extraLen = view.getUint16(pos + 30, true)
    const commentLen = view.getUint16(pos + 32, true)
    const localHeaderOffset = view.getUint32(pos + 42, true)
    const fileName = new TextDecoder().decode(bytes.slice(pos + 46, pos + 46 + fileNameLen))

    if (fileName === targetName) {
      // 从 Local File Header 获取实际数据偏移
      const localFileNameLen = view.getUint16(localHeaderOffset + 26, true)
      const localExtraLen = view.getUint16(localHeaderOffset + 28, true)
      const dataStart = localHeaderOffset + 30 + localFileNameLen + localExtraLen
      const compressedData = bytes.slice(dataStart, dataStart + compressedSize)

      if (compressionMethod === 0) {
        // Store（无压缩）
        return new TextDecoder('utf-8').decode(compressedData)
      } else if (compressionMethod === 8) {
        // Deflate
        return await inflateRaw(compressedData)
      }
    }

    pos += 46 + fileNameLen + extraLen + commentLen
  }

  return null
}

/**
 * 使用 DecompressionStream 解压 deflate-raw 数据
 */
async function inflateRaw(compressedData) {
  const ds = new DecompressionStream('deflate-raw')
  const writer = ds.writable.getWriter()
  const reader = ds.readable.getReader()

  writer.write(compressedData)
  writer.close()

  const chunks = []
  let done = false
  while (!done) {
    const result = await reader.read()
    if (result.value) chunks.push(result.value)
    done = result.done
  }

  const totalLen = chunks.reduce((sum, c) => sum + c.length, 0)
  const merged = new Uint8Array(totalLen)
  let offset = 0
  for (const chunk of chunks) {
    merged.set(chunk, offset)
    offset += chunk.length
  }

  return new TextDecoder('utf-8').decode(merged)
}

// ========== XLSX 解析 ==========

/**
 * 将列字母（A, B, ..., Z, AA, AB, ...）转为数字索引（从1开始）
 */
function colLettersToNumber(col) {
  let num = 0
  for (let i = 0; i < col.length; i++) {
    num = num * 26 + (col.charCodeAt(i) - 64)
  }
  return num
}

/**
 * 从 XML sheet 文本中提取单元格的纯文本（处理 <t> 和 <is> 标签）
 */
function getCellText(cellEl) {
  // 优先取 <v> 标签
  const vEl = cellEl.getElementsByTagName('v')[0]
  // 也检查 <is><t> 内联字符串
  const isEl = cellEl.getElementsByTagName('is')[0]
  if (isEl) {
    const tEls = isEl.getElementsByTagName('t')
    let text = ''
    for (let j = 0; j < tEls.length; j++) {
      text += (tEls[j].textContent || '')
    }
    return text
  }
  return vEl ? vEl.textContent : ''
}

/**
 * 解析 XLSX 文件，返回二维数组（每行为一个数组）
 */
async function parseXlsx(file) {
  const buffer = await file.arrayBuffer()
  const parser = new DOMParser()
  console.log('[xlsxParser] XLSX ArrayBuffer 大小:', buffer.byteLength, 'bytes')

  // 1. 解析共享字符串
  const sharedStringsXml = await extractZipEntry(buffer, 'xl/sharedStrings.xml')
  const sharedStrings = []
  if (sharedStringsXml) {
    const ssDoc = parser.parseFromString(sharedStringsXml, 'application/xml')
    const siElements = ssDoc.getElementsByTagName('si')
    for (let i = 0; i < siElements.length; i++) {
      const tEls = siElements[i].getElementsByTagName('t')
      let text = ''
      for (let j = 0; j < tEls.length; j++) {
        text += (tEls[j].textContent || '')
      }
      sharedStrings.push(text)
    }
    console.log('[xlsxParser] 共享字符串表（sharedStrings）共 ' + sharedStrings.length + ' 条:', sharedStrings)
  } else {
    console.log('[xlsxParser] 无共享字符串表（sharedStrings.xml 不存在）')
  }

  // 2. 解析第一个 sheet
  const sheetXml = await extractZipEntry(buffer, 'xl/worksheets/sheet1.xml')
  if (!sheetXml) {
    throw new Error('未找到工作表数据')
  }
  console.log('[xlsxParser] sheet1.xml 原始内容（前500字）:', sheetXml.substring(0, 500))

  const sheetDoc = parser.parseFromString(sheetXml, 'application/xml')
  const rows = sheetDoc.getElementsByTagName('row')
  console.log('[xlsxParser] sheet1 中检测到 ' + rows.length + ' 行')

  // 3. 构建行列数据
  const allRows = []
  let maxCol = 0

  for (let i = 0; i < rows.length; i++) {
    const cells = rows[i].getElementsByTagName('c')
    const rowData = {}

    for (let j = 0; j < cells.length; j++) {
      const cell = cells[j]
      const ref = cell.getAttribute('r') // 如 "A1", "B2"
      const type = cell.getAttribute('t')

      if (!ref) continue
      const colLetters = ref.replace(/[0-9]/g, '')
      const colNum = colLettersToNumber(colLetters)
      if (colNum > maxCol) maxCol = colNum

      let value = ''
      if (type === 's') {
        // 共享字符串引用
        const idx = parseInt(getCellText(cell), 10)
        value = sharedStrings[idx] || ''
      } else if (type === 'inlineStr') {
        value = getCellText(cell)
      } else {
        value = getCellText(cell)
      }

      rowData[colNum] = value
    }

    // 转为数组
    const rowArr = []
    for (let c = 1; c <= maxCol; c++) {
      rowArr.push(rowData[c] || '')
    }
    allRows.push(rowArr)
  }

  console.log('[xlsxParser] XLSX 解析完成，共 ' + allRows.length + ' 行，最大列数:', maxCol)
  return allRows
}

// ========== CSV 解析 ==========

/**
 * 解析 CSV 文件，返回二维数组
 */
function parseCsv(text) {
  console.log('[xlsxParser] CSV 文本长度:', text.length, '字符')
  const lines = text.split(/\r?\n/).filter(l => l.trim())
  console.log('[xlsxParser] CSV 共 ' + lines.length + ' 行（含表头）')
  const result = lines.map(line => {
    // 简单 CSV 解析（处理引号内的逗号）
    const result = []
    let current = ''
    let inQuotes = false

    for (let i = 0; i < line.length; i++) {
      const ch = line[i]
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"'
          i++
        } else {
          inQuotes = !inQuotes
        }
      } else if (ch === ',' && !inQuotes) {
        result.push(current.trim())
        current = ''
      } else {
        current += ch
      }
    }
    result.push(current.trim())
    return result
  })
  console.log('[xlsxParser] CSV 解析完成，共 ' + result.length + ' 行')
  return result
}

// ========== 公共接口 ==========

/**
 * 解析 Excel/CSV 文件为结构化脚本数据
 * @param {File} file - .xlsx 或 .csv 文件
 * @returns {Array} 解析后的脚本数据数组
 */
export async function parseExcelFile(file) {
  const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase()
  console.log('[xlsxParser] 开始解析文件:', {
    name: file.name,
    size: file.size + ' bytes',
    type: file.type,
    ext
  })

  let rows = []

  if (ext === '.xlsx') {
    rows = await parseXlsx(file)
  } else if (ext === '.csv') {
    const text = await file.text()
    rows = parseCsv(text)
  } else if (ext === '.xls') {
    throw new Error('.xls 旧格式不支持，请转换为 .xlsx 后重试')
  } else {
    throw new Error('不支持的文件格式')
  }

  console.log('[xlsxParser] 原始行数据（含表头）:', rows)

  if (rows.length < 2) {
    throw new Error('文件内容为空或缺少数据行')
  }

  // 第一行为表头，后续为数据
  const header = rows[0]
  console.log('[xlsxParser] 表头:', header)

  const result = rows.slice(1).map((row, idx) => ({
    index: row[0] || String(idx + 1),
    description: row[1] || '',
    shotType: row[2] || '',
    angle: row[3] || '',
    cameraMove: row[4] || '',
    scriptSlot: row[5] || ''
  }))

  console.log('[xlsxParser] 解析完成，共 ' + result.length + ' 条数据:', result)
  return result
}
